// ISDP CUSTOM: Entire file — Component entity "API" tab card
// Reads isdp.io/api-endpoints annotation and renders consumed API endpoints with method badges
import { useEntity } from '@backstage/plugin-catalog-react';
import { Link } from 'react-router-dom';
import './ApiEndpointsCard.css';
import { methodColor } from './methodColors';

const ANNOTATION_KEY = 'isdp.io/api-endpoints';

interface ParsedEndpoint {
  method: string;
  path: string;
}

interface ApiEndpoints {
  apiName: string;
  endpoints: ParsedEndpoint[];
}

function parseAnnotation(value: string): ApiEndpoints[] {
  const result: ApiEndpoints[] = [];
  for (const line of value.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx === -1) continue;
    const apiName = trimmed.substring(0, colonIdx).trim();
    const endpointsStr = trimmed.substring(colonIdx + 1).trim();
    const endpoints: ParsedEndpoint[] = endpointsStr
      .split(',')
      .map(ep => ep.trim())
      .filter(Boolean)
      .map(ep => {
        const spaceIdx = ep.indexOf(' ');
        if (spaceIdx === -1) return { method: 'GET', path: ep };
        return {
          method: ep.substring(0, spaceIdx).toUpperCase(),
          path: ep.substring(spaceIdx + 1).trim(),
        };
      });
    if (endpoints.length > 0) {
      result.push({ apiName, endpoints });
    }
  }
  return result;
}

export function ApiEndpointsCard() {
  const { entity } = useEntity();
  const annotation = entity.metadata.annotations?.[ANNOTATION_KEY];

  if (!annotation) {
    return null;
  }

  const apiEndpoints = parseAnnotation(annotation);

  if (apiEndpoints.length === 0) {
    return null;
  }

  return (
    <div className="devhub-api-endpoints-card">
      <div className="devhub-api-endpoints-card__header">
        Endpoint Consumption
      </div>
      <hr className="devhub-api-endpoints-card__divider" />
      <div className="devhub-api-endpoints-card__body">
        {apiEndpoints.map(({ apiName, endpoints }) => {
          const namespace = entity.metadata.namespace || 'default';
          const apiPath = `/catalog/${namespace}/api/${apiName}`;

          return (
            <div key={apiName} className="devhub-api-endpoints-card__api-section">
              <Link to={apiPath} className="devhub-api-endpoints-card__api-link">
                {apiName}
              </Link>
              <table className="devhub-api-endpoints-table">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoints.map((ep, i) => (
                    <tr key={i}>
                      <td>
                        <span
                          className="devhub-method-badge"
                          style={{
                            backgroundColor: methodColor(ep.method),
                          }}
                        >
                          {ep.method}
                        </span>
                      </td>
                      <td className="devhub-api-endpoints-table__path">
                        {ep.path}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}
